import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { doc, updateDoc, setDoc, getDoc, increment, runTransaction, collection, serverTimestamp } from 'firebase/firestore';
import PadelScoreBoard from './PadelScoreBoard';
import HandTracker from './HandTracker';
import DwellCursor from './DwellCursor';
import CelebrationOverlay from './CelebrationOverlay';
import SettingsModal from './SettingsModal';
import { synth } from '../../engine/audioSynthesizer';
import { getPlayerAvatar } from '../../utils/player';
import { getDeviceType, getDeviceId, sanitizeScore } from '../../utils/scoringAudit';
import { isNativeApp } from '../../utils/platform';
import { computePointResult, computeUndoResult, pointsToDisplay } from '../../utils/classicScore';

const getFirstName = (fullName) => (fullName || '').trim().split(' ')[0] || '';

const scoreToWord = (s) => {
  if (s === '0' || s === '-') return 'love';
  if (s === '15') return 'fifteen';
  if (s === '30') return 'thirty';
  if (s === '40') return 'forty';
  if (s === 'A' || s === 'AD') return 'advantage';
  return 'love';
};

let liveSessionSequence = 0;

export const isTeam1Serving = (servingPlayer, team1Players) => {
  if (!servingPlayer) return true;
  const sName = (typeof servingPlayer === 'string' ? servingPlayer : servingPlayer?.name || '').trim().toLowerCase();
  const t1Names = (team1Players || []).map((p) => (typeof p === 'string' ? p : p?.name || '').trim().toLowerCase());
  return t1Names.includes(sName);
};

const getScorePhrase = (serverScore, receiverScore, serverTeamNames, receiverTeamNames) => {
  if (serverScore === '40' && receiverScore === '40') return 'deuce';
  if ((serverScore === 'A' || serverScore === 'AD') && receiverScore === '40') {
    return serverTeamNames.length ? `Advantage ${serverTeamNames.join(' and ')}` : 'Advantage';
  }
  if (serverScore === '40' && (receiverScore === 'A' || receiverScore === 'AD')) {
    return receiverTeamNames.length ? `Advantage ${receiverTeamNames.join(' and ')}` : 'Advantage';
  }
  const serverWord = scoreToWord(serverScore);
  const receiverWord = scoreToWord(receiverScore);
  if (serverWord === receiverWord) return `${serverWord} all`;
  return `${serverWord} ${receiverWord}`;
};

export default function LiveSessionClassic({
  classicMatch,
  classicScore,
  sessionVersion = 0,
  classicPointHistory = [],
  classicGameHistory = [],
  bestOfSets = 3,
  goldenPoint: goldenPointProp,
  onToggleGoldenPoint: onToggleGoldenPointProp,
  clubName: clubNameProp,
  db,
  selectedClubId,
  appId,
  allPlayers = [],
  onMatchWin,
  onFinishSession = () => {},
}) {
  // Kept in step with localScore (not read straight from props), so an undo right after this device's
  // own point removes that point even before its snapshot has arrived
  const [localPointHistory, setLocalPointHistory] = useState(classicPointHistory || []);
  const pointHistory = localPointHistory;
  const [pairingCode, setPairingCode] = useState(null);
  const [isHandTrackingEnabled, setIsHandTrackingEnabledState] = useState(() => {
    if (!isNativeApp) return false; // hand tracking is native-app only
    try {
      const saved = localStorage.getItem('padelmvp_hand_tracking_enabled');
      return saved !== null ? JSON.parse(saved) : false; // off by default; opt in via Settings
    } catch (e) {
      return false;
    }
  });

  const setIsHandTrackingEnabled = (valOrFn) => {
    setIsHandTrackingEnabledState((prev) => {
      const nextVal = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        localStorage.setItem('padelmvp_hand_tracking_enabled', JSON.stringify(nextVal));
      } catch (e) {
        console.warn('Failed to save hand tracking preference:', e);
      }
      return nextVal;
    });
  };
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [localGoldenPoint, setLocalGoldenPoint] = useState(() => {
    try {
      const saved = localStorage.getItem('padelmvp_golden_point');
      return saved !== null ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  });

  const goldenPoint = goldenPointProp !== undefined ? goldenPointProp : localGoldenPoint;

  const handleToggleGoldenPoint = () => {
    if (onToggleGoldenPointProp) {
      onToggleGoldenPointProp();
      return;
    }
    setLocalGoldenPoint((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('padelmvp_golden_point', JSON.stringify(next));
      } catch (e) {}
      if (db && selectedClubId) {
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        updateDoc(sessionDocRef, { goldenPoint: next }).catch(() => {});
      }
      return next;
    });
  };
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(() => {
    const saved = localStorage.getItem('padel_speech_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [celebrationData, setCelebrationData] = useState(null);
  const [cursorData, setCursorData] = useState(null);
  const [statusText, setStatusText] = useState('Initializing AI Vision...');

  useEffect(() => {
    synth.setSpeechEnabled(isSpeechEnabled);
  }, [isSpeechEnabled]);

  const handleToggleSpeech = () => {
    setIsSpeechEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('padel_speech_enabled', String(next));
      synth.setSpeechEnabled(next);
      return next;
    });
  };

  const lastSpokenScoresRef = useRef(null);
  const wakeLockRef = useRef(null);
  const handlePointRef = useRef(null);
  const handleUndoRef = useRef(null);
  const pressPatternRef = useRef({ count: 0, timeoutId: null });
  const gameHistoryRef = useRef(classicGameHistory || []);
  const lastGameTimeRef = useRef(null);
  const matchStartTimeRef = useRef(new Date().toISOString());
  const matchEndedRef = useRef(false);

  useEffect(() => {
    if (classicGameHistory && classicGameHistory.length > 0 && gameHistoryRef.current.length === 0) {
      gameHistoryRef.current = classicGameHistory;
    }
  }, [classicGameHistory]);

  const [localScore, setLocalScore] = useState(classicScore || { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] });

  // This device's score writes still in flight, and the session version its latest write produced.
  // A snapshot that arrives during a write, or carries an older version, predates the score this
  // device already shows (e.g. the late echo of the previous point on a slow network), so the sync
  // effect below doesn't apply it; the snapshot of our own write follows.
  const localWritesRef = useRef({ pending: 0, version: 0 });
  const latestSnapshotRef = useRef({ score: classicScore, history: classicPointHistory, version: sessionVersion });
  const trackLocalWrite = async (write) => {
    localWritesRef.current.pending += 1;
    try {
      const version = await write();
      if (typeof version === 'number') {
        localWritesRef.current.version = Math.max(localWritesRef.current.version, version);
      }
    } finally {
      localWritesRef.current.pending -= 1;
      // Our write's snapshot may have landed while it was in flight; catch up with it (or a newer one) now
      const latest = latestSnapshotRef.current;
      if (!localWritesRef.current.pending && latest.score && latest.version >= localWritesRef.current.version) {
        setLocalScore(latest.score);
        setLocalPointHistory(latest.history || []);
      }
    }
  };

  const activeScore = localScore || classicScore || { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] };
  const sets = activeScore?.sets || [0, 0];
  const games = activeScore?.games || [0, 0];
  const points = [activeScore?.points?.[0] ?? 0, activeScore?.points?.[1] ?? 0];
  const isTieBreak = activeScore?.isTieBreak ?? false;
  const toName = (p) => (typeof p === 'string' ? p : p?.name || String(p || ''));
  // Keyed on the names so the arrays keep their identity between renders; otherwise the
  // Firestore sync effect below re-runs on every render and reverts each local point to the last snapshot
  const team1Key = JSON.stringify((classicMatch?.team1 || (allPlayers.length >= 2 ? [allPlayers[0], allPlayers[1]] : ['Team 1 A', 'Team 1 B'])).map(toName));
  const team2Key = JSON.stringify((classicMatch?.team2 || (allPlayers.length >= 4 ? [allPlayers[2], allPlayers[3]] : ['Team 2 A', 'Team 2 B'])).map(toName));
  const team1 = useMemo(() => JSON.parse(team1Key), [team1Key]);
  const team2 = useMemo(() => JSON.parse(team2Key), [team2Key]);

  const [localServer, setLocalServer] = useState(classicMatch?.server || null);

  useEffect(() => {
    if (classicMatch?.server) {
      setLocalServer(classicMatch.server);
    }
  }, [classicMatch?.server]);

  const server = localServer || classicMatch?.server || team1[0] || 'Server';
  const servingTeamNumber = isTeam1Serving(server, team1) ? 1 : 2;

  const announceClassicScore = (d1, d2, isTb, srvTeamNum) => {
    const stNum = srvTeamNum ?? servingTeamNumber;
    const [servingScore, receiverScore] = stNum === 1 ? [d1, d2] : [d2, d1];
    if (isTb) {
      synth.speakScore(`${servingScore} ${receiverScore}`);
    } else {
      const t1Names = team1.map((p) => getFirstName(p)).filter(Boolean);
      const t2Names = team2.map((p) => getFirstName(p)).filter(Boolean);
      const [servingNames, receiverNames] = stNum === 1 ? [t1Names, t2Names] : [t2Names, t1Names];
      const phrase = getScorePhrase(servingScore, receiverScore, servingNames, receiverNames);
      synth.speakScore(phrase);
    }
  };
  const announceClassicScoreRef = useRef(announceClassicScore);
  announceClassicScoreRef.current = announceClassicScore;
  const onMatchWinRef = useRef(onMatchWin);
  onMatchWinRef.current = onMatchWin;

  // Server rotation helpers
  const getNextServer = (curServer, curScore) => {
    const rotation = [team1[0], team2[0], team1[1], team2[1]].filter(Boolean);
    if (rotation.length < 2) return curServer;
    const curIdx = rotation.indexOf(curServer);
    if (curIdx >= 0) {
      return rotation[(curIdx + 1) % rotation.length];
    }
    let totalGames = (curScore?.games?.[0] || 0) + (curScore?.games?.[1] || 0);
    if (Array.isArray(curScore?.setScores)) {
      for (const s of curScore.setScores) {
        totalGames += (s.team1 ?? s[0] ?? 0) + (s.team2 ?? s[1] ?? 0);
      }
    }
    return rotation[totalGames % rotation.length];
  };

  const getPreviousServer = (curServer) => {
    const rotation = [team1[0], team2[0], team1[1], team2[1]].filter(Boolean);
    if (rotation.length < 2) return curServer;
    const curIdx = rotation.indexOf(curServer);
    if (curIdx >= 0) {
      return rotation[(curIdx - 1 + rotation.length) % rotation.length];
    }
    return rotation[0];
  };

  // Screen WakeLock to prevent device sleep during live match
  useEffect(() => {
    let cancelled = false;

    const requestWakeLock = async () => {
      if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          await lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      } catch (e) {
        // Ignore wake lock errors
      }
    };

    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Ensure 6-digit pairing code for smartwatch companion
  useEffect(() => {
    if (!db || !selectedClubId || !appId) return;
    const genCode = () => String(Math.floor(100000 + Math.random() * 900000));
    const sessionDocRef = doc(
      db,
      'artifacts',
      appId,
      'public',
      'data',
      'clubs',
      selectedClubId,
      'sessions',
      'current'
    );

    const ensurePairingCode = async () => {
      const clubName = typeof clubNameProp === 'string' ? clubNameProp : 'Club';
      try {
        const snap = await getDoc(sessionDocRef);
        const data = snap.exists() ? snap.data() : {};
        let code = data.pairingCode;
        if (!code) {
          code = genCode();
        }
        const expiresAt = Date.now() + 12 * 60 * 60 * 1000;
        const pairingCodesRef = doc(
          db,
          'artifacts',
          appId,
          'public',
          'data',
          'pairingCodes',
          code
        );

        await updateDoc(sessionDocRef, { pairingCode: code, pairingCodeExpires: expiresAt });
        setPairingCode(code);

        await setDoc(
          pairingCodesRef,
          { clubId: selectedClubId, clubName, expiresAt },
          { merge: true }
        );
      } catch (e) {
        console.error('Failed to ensure pairing code for classic session:', e);
      }
    };

    ensurePairingCode();
  }, [db, selectedClubId, appId, clubNameProp]);

  const [disp1, disp2] = pointsToDisplay(points[0], points[1], isTieBreak);
  const team1PointsDisplay = disp1 ?? (isTieBreak ? String(points[0]) : '0');
  const team2PointsDisplay = disp2 ?? (isTieBreak ? String(points[1]) : '0');

  // Firestore Array helper
  const toFirestoreSetScores = (setScores) =>
    (setScores || []).map((pair) =>
      Array.isArray(pair) ? { team1: pair[0], team2: pair[1] } : { team1: pair.team1, team2: pair.team2 }
    );

  const persistClassicScore = async (newScore, newHistory, nextServer) => {
    if (!db || !selectedClubId || !appId) return;
    try {
      const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
      const classicScoreForFirestore = newScore.setScores != null
        ? { ...newScore, setScores: toFirestoreSetScores(newScore.setScores) }
        : newScore;
      const payload = {
        classicScore: classicScoreForFirestore,
        classicGameHistory: gameHistoryRef.current || [],
        bestOfSets: typeof bestOfSets === 'number' ? bestOfSets : 3,
        updatedAt: new Date().toISOString(),
        lastWriteDevice: getDeviceType(),
        version: increment(1)
      };
      if (newHistory !== undefined) payload.classicPointHistory = newHistory;
      if (nextServer && classicMatch) {
        const nextMatch = { ...classicMatch, server: nextServer };
        payload.gameRotation = [{ ...nextMatch }];
      }
      await updateDoc(sessionDocRef, payload);
    } catch (e) {
      console.error('Error persisting classic score:', e);
    }
  };

  const persistClassicPointTransactional = async (scoringTeam, fallbackResult, nextServer) => {
    if (!db || !selectedClubId || !appId) return;
    const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
    const eventsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current', 'events');

    try {
      // Resolves with the session version this write produces (see trackLocalWrite)
      return await runTransaction(db, async (txn) => {
        const snap = await txn.get(sessionDocRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const baseScore = data?.classicScore || activeScore || { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] };
        const baseHistory = data?.classicPointHistory || pointHistory || [];
        const baseBestOf = typeof data?.bestOfSets === 'number' ? data.bestOfSets : (typeof bestOfSets === 'number' ? bestOfSets : 3);
        const baseGolden = Boolean(data?.goldenPoint);
        const curRot = data?.gameRotation?.[0] || classicMatch;
        const curServer = typeof curRot?.server === 'string' ? curRot.server : server;

        const res = computePointResult(baseScore, scoringTeam, baseBestOf, baseHistory, baseGolden, curServer);
        if (!res) return;

        const { newScore, newHistory, matchWinner, setJustWon, gameJustWon } = res;

        // Server rotation
        let nextRotation = null;
        if (gameJustWon && curRot) {
          const t1 = (curRot.team1 || team1 || []).map(toName);
          const t2 = (curRot.team2 || team2 || []).map(toName);
          const rotation = [t1[0], t2[0], t1[1], t2[1]].filter(Boolean);
          if (rotation.length >= 2) {
            const curIdx = rotation.indexOf(curServer);
            const nServer = curIdx >= 0
              ? rotation[(curIdx + 1) % rotation.length]
              : rotation[(((baseScore.games?.[0] || 0) + (baseScore.games?.[1] || 0) + 1)) % rotation.length];
            nextRotation = [{ ...curRot, server: nServer }];
          }
        }

        const classicScoreForFirestore = newScore.setScores != null
          ? { ...newScore, setScores: toFirestoreSetScores(newScore.setScores) }
          : newScore;

        const nowIso = new Date().toISOString();
        const payload = {
          classicScore: classicScoreForFirestore,
          classicPointHistory: newHistory,
          classicGameHistory: gameHistoryRef.current || [],
          bestOfSets: baseBestOf,
          updatedAt: nowIso,
          lastWriteDevice: getDeviceType(),
          version: increment(1)
        };
        if (nextRotation) payload.gameRotation = nextRotation;

        txn.update(sessionDocRef, payload);

        // Record audit event in same transaction
        const action = matchWinner ? 'match_won' : setJustWon ? 'set_won' : gameJustWon ? 'game_won' : 'point';
        const eventDocRef = doc(eventsCollectionRef);
        liveSessionSequence += 1;
        txn.set(eventDocRef, {
          action,
          device: getDeviceType(),
          deviceId: getDeviceId(),
          clientTs: Date.now(),
          clientTime: nowIso,
          ts: serverTimestamp(),
          sequence: liveSessionSequence,
          scoringTeam,
          server: curServer,
          before: sanitizeScore(baseScore),
          after: sanitizeScore(newScore),
          bestOfSets: baseBestOf,
          note: null
        });

        return (typeof data?.version === 'number' ? data.version : 0) + 1;
      });
    } catch (e) {
      console.warn('Transaction failed, falling back to persistClassicScore:', e);
      await persistClassicScore(fallbackResult?.newScore, fallbackResult?.newHistory, nextServer);
    }
  };

  const persistUndoTransactional = async (fallbackScore, fallbackHistory, fallbackServer) => {
    if (!db || !selectedClubId || !appId) return;
    const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
    const eventsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current', 'events');

    try {
      return await runTransaction(db, async (txn) => {
        const snap = await txn.get(sessionDocRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const baseScore = data?.classicScore || activeScore;
        const baseHistory = data?.classicPointHistory || pointHistory || [];
        const res = computeUndoResult(baseScore, baseHistory);
        if (!res) return;

        const classicScoreForFirestore = res.newScore.setScores != null
          ? { ...res.newScore, setScores: toFirestoreSetScores(res.newScore.setScores) }
          : res.newScore;

        let nextGameHistory = data?.classicGameHistory || gameHistoryRef.current || [];
        if (res.gameUndone && nextGameHistory.length > 0) {
          nextGameHistory = nextGameHistory.slice(0, -1);
        }

        const curRot = data?.gameRotation?.[0] || classicMatch;
        const curServer = typeof curRot?.server === 'string' ? curRot.server : server;
        const targetServer = res.previousServer || fallbackServer || (res.gameUndone ? getPreviousServer(curServer) : curServer);

        const nowIso = new Date().toISOString();
        const payload = {
          classicScore: classicScoreForFirestore,
          classicPointHistory: res.newHistory,
          classicGameHistory: nextGameHistory,
          updatedAt: nowIso,
          lastWriteDevice: getDeviceType(),
          version: increment(1)
        };

        if (targetServer && curRot) {
          payload.gameRotation = [{ ...curRot, server: targetServer }];
        }

        txn.update(sessionDocRef, payload);

        const eventDocRef = doc(eventsCollectionRef);
        liveSessionSequence += 1;
        txn.set(eventDocRef, {
          action: 'undo',
          device: getDeviceType(),
          deviceId: getDeviceId(),
          clientTs: Date.now(),
          clientTime: nowIso,
          ts: serverTimestamp(),
          sequence: liveSessionSequence,
          scoringTeam: null,
          server: targetServer || null,
          before: sanitizeScore(baseScore),
          after: sanitizeScore(res.newScore),
          bestOfSets: typeof data?.bestOfSets === 'number' ? data.bestOfSets : (typeof bestOfSets === 'number' ? bestOfSets : 3),
          note: null
        });
        return (typeof data?.version === 'number' ? data.version : 0) + 1;
      });
    } catch (e) {
      console.warn('Undo transaction failed, falling back to updateDoc:', e);
      await persistClassicScore(fallbackScore, fallbackHistory, fallbackServer);
    }
  };

  const localActionTimestampRef = useRef(0);
  const prevClassicScoreRef = useRef(classicScore);

  // Sync prop changes from outside and announce remote points/games/celebrations
  useEffect(() => {
    if (!classicScore) return;
    const prev = prevClassicScoreRef.current;
    prevClassicScoreRef.current = classicScore;
    latestSnapshotRef.current = { score: classicScore, history: classicPointHistory, version: sessionVersion };
    const { pending, version } = localWritesRef.current;
    if (!pending && sessionVersion >= version) {
      setLocalScore(classicScore);
      setLocalPointHistory(classicPointHistory || []);
    }

    const isRemote = Date.now() - localActionTimestampRef.current > 1500;
    if (isRemote && prev) {
      const p1 = classicScore.points?.[0] || 0;
      const p2 = classicScore.points?.[1] || 0;
      const prevP1 = prev.points?.[0] || 0;
      const prevP2 = prev.points?.[1] || 0;

      const g1 = classicScore.games?.[0] || 0;
      const g2 = classicScore.games?.[1] || 0;
      const prevG1 = prev.games?.[0] || 0;
      const prevG2 = prev.games?.[1] || 0;

      const s1 = classicScore.sets?.[0] || 0;
      const s2 = classicScore.sets?.[1] || 0;
      const prevS1 = prev.sets?.[0] || 0;
      const prevS2 = prev.sets?.[1] || 0;

      const isGamesUndone = g1 < prevG1 || g2 < prevG2;
      const isSetsUndone = s1 < prevS1 || s2 < prevS2;
      const isPointsUndone = (p1 < prevP1 || p2 < prevP2) && g1 === prevG1 && g2 === prevG2 && s1 === prevS1 && s2 === prevS2;
      const isUndone = isGamesUndone || isSetsUndone || isPointsUndone;

      if (isUndone) {
        synth.init();
        synth.playUndo();
        setCelebrationData(null);
        matchEndedRef.current = false;
        const [d1, d2] = pointsToDisplay(p1, p2, classicScore?.isTieBreak);
        announceClassicScoreRef.current(d1, d2, classicScore?.isTieBreak);
        return;
      }

      const isGameWon = g1 > prevG1 || g2 > prevG2 || s1 > prevS1 || s2 > prevS2;
      const isPointScored = (p1 > prevP1 || p2 > prevP2) && !isGameWon;

      if (isPointScored) {
        synth.init();
        const scoringTeam = p1 > prevP1 ? 1 : 2;
        synth.playPoint(scoringTeam === 1 ? 'A' : 'B');
        const [d1, d2] = pointsToDisplay(p1, p2, classicScore?.isTieBreak);
        announceClassicScoreRef.current(d1, d2, classicScore?.isTieBreak);
      } else if (isGameWon) {
        synth.init();
        const setsToWin = Math.ceil(bestOfSets / 2);
        const gameWinner = (g1 > prevG1 || s1 > prevS1) ? 1 : 2;
        const winningTeam = gameWinner === 1 ? team1 : team2;
        const winnerNames = winningTeam.map((p) => getFirstName(p)).filter(Boolean).join(' and ');

        const setWinner = s1 >= setsToWin ? 1 : s2 >= setsToWin ? 2 : null;
        if (setWinner) {
          matchEndedRef.current = true;
          synth.playMatch();
          setCelebrationData({
            type: 'MATCH',
            setScores: classicScore.setScores || [],
            team: setWinner,
            teamAName: team1.join(' & '),
            teamBName: team2.join(' & '),
            teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
            teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
          });
          if (onMatchWinRef.current) {
            onMatchWinRef.current(setWinner, classicScore, gameHistoryRef.current);
          }
        } else if (s1 !== prevS1 || s2 !== prevS2) {
          synth.playSet();
          setCelebrationData({
            type: 'SET',
            setScores: classicScore.setScores || [],
            team: gameWinner,
            teamAName: team1.join(' & '),
            teamBName: team2.join(' & '),
            teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
            teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
          });
        } else {
          synth.playGame();
          setCelebrationData({
            type: 'GAME',
            games: [g1, g2],
            team: gameWinner,
            teamAName: team1.join(' & '),
            teamBName: team2.join(' & '),
            teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
            teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
          });
        }

        const winningGames = g1 > g2 ? g1 : g2;
        const losingGames = g1 > g2 ? g2 : g1;
        let gamePhrase = '';
        if (g1 === g2) {
          gamePhrase = `Game ${winnerNames}. ${g1} games all.`;
        } else {
          const gameWord = winningGames === 1 ? 'game' : 'games';
          const loseWord = losingGames === 0 ? 'love' : losingGames;
          gamePhrase = `Game ${winnerNames}. ${winnerNames} leading ${winningGames} ${gameWord} to ${loseWord}.`;
        }
        synth.speakScore(gamePhrase);
      }
    }
  }, [classicScore, classicPointHistory, sessionVersion, team1, team2, allPlayers, bestOfSets]);

  // Point scoring state machine (delegated to pure classicScore engine)
  const handlePoint = (scoringTeam) => {
    localActionTimestampRef.current = Date.now();
    if (matchEndedRef.current) return;
    synth.init();

    const result = computePointResult(activeScore, scoringTeam, bestOfSets, pointHistory, goldenPoint, server);
    if (!result) return;
    setLocalPointHistory(result.newHistory);

    const {
      newScore,
      matchWinner,
      setWinner,
      gameWinner,
      setJustWon,
      gameJustWon,
      p1,
      p2,
      g1,
      g2,
    } = result;

    if (gameJustWon) {
      // Speak Game Winner Announcement immediately
      const winningTeam = gameWinner === 1 ? team1 : team2;
      const winnerNames = winningTeam.map((p) => getFirstName(p)).filter(Boolean).join(' and ');
      const winningGames = g1 > g2 ? g1 : g2;
      const losingGames = g1 > g2 ? g2 : g1;
      let gamePhrase = '';
      if (g1 === g2) {
        gamePhrase = `Game ${winnerNames}. ${g1} games all.`;
      } else {
        const gameWord = winningGames === 1 ? 'game' : 'games';
        const loseWord = losingGames === 0 ? 'love' : losingGames;
        gamePhrase = `Game ${winnerNames}. ${winnerNames} leading ${winningGames} ${gameWord} to ${loseWord}.`;
      }
      synth.speakScore(gamePhrase);
      lastSpokenScoresRef.current = '0-0';

      // Record individual game log
      const nowIso = new Date().toISOString();
      const lastTime = lastGameTimeRef.current || matchStartTimeRef.current || nowIso;
      lastGameTimeRef.current = nowIso;

      const winPts = p1 > p2 ? p1 : p2;
      const losePts = p1 > p2 ? p2 : p1;
      const toGamePointLabel = (pts) => {
        if (isTieBreak) return String(pts);
        if (pts === 0) return '0';
        if (pts === 1) return '15';
        if (pts === 2) return '30';
        if (pts >= 3) return '40';
        return '0';
      };

      const gameRecord = {
        winningTeam: gameWinner === 1 ? team1 : team2,
        losingTeam: gameWinner === 1 ? team2 : team1,
        serving: server || 'N/A',
        gameStartTime: lastTime,
        timestamp: nowIso,
        scores: {
          winningTeam: toGamePointLabel(winPts),
          losingTeam: toGamePointLabel(losePts),
        },
        classicMatch: true,
        setNumber: (sets[0] || 0) + (sets[1] || 0) + 1,
        gameNumberInSet: (g1 || 0) + (g2 || 0),
      };
      gameHistoryRef.current = [...(gameHistoryRef.current || []), gameRecord];

      const nextServer = getNextServer(server, newScore);

      if (matchWinner) {
        matchEndedRef.current = true;
        synth.playMatch();
        setLocalScore(newScore);
        // Archive only once the winning point (and its match_won event) is written, so the archive includes it
        const pointSaved = trackLocalWrite(() => persistClassicPointTransactional(scoringTeam, result, nextServer));
        setCelebrationData({
          type: 'MATCH',
          setScores: newScore.setScores,
          team: matchWinner,
          server: server,
          teamAName: team1.join(' & '),
          teamBName: team2.join(' & '),
          teamAPlayers: team1,
          teamBPlayers: team2,
          teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
          teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
        });
        pointSaved.finally(() => onMatchWin(matchWinner, newScore, gameHistoryRef.current));
        return;
      }

      if (setJustWon) {
        synth.playSet();
        setCelebrationData({
          type: 'SET',
          setScores: newScore.setScores,
          team: setWinner,
          server: server,
          teamAName: team1.join(' & '),
          teamBName: team2.join(' & '),
          teamAPlayers: team1,
          teamBPlayers: team2,
          teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
          teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
        });
      } else {
        synth.playGame();
        setCelebrationData({
          type: 'GAME',
          games: newScore.games,
          team: gameWinner,
          server: server,
          teamAName: team1.join(' & '),
          teamBName: team2.join(' & '),
          teamAPlayers: team1,
          teamBPlayers: team2,
          teamAAvatars: team1.map((name) => getPlayerAvatar(allPlayers, name)),
          teamBAvatars: team2.map((name) => getPlayerAvatar(allPlayers, name)),
        });
      }

      setLocalScore(newScore);
      setLocalServer(nextServer);
      trackLocalWrite(() => persistClassicPointTransactional(scoringTeam, result, nextServer));
      return;
    }

    // Normal point scored
    synth.playPoint(scoringTeam === 1 ? 'A' : 'B');

    // Announce point score
    const [d1, d2] = pointsToDisplay(p1, p2, isTieBreak);
    announceClassicScore(d1, d2, isTieBreak);

    setLocalScore(newScore);
    trackLocalWrite(() => persistClassicPointTransactional(scoringTeam, result));
  };

  const handleUndo = () => {
    localActionTimestampRef.current = Date.now();
    matchEndedRef.current = false;
    synth.init();
    if (!pointHistory.length) return;
    synth.playUndo();

    const undoResult = computeUndoResult(activeScore, pointHistory);
    if (!undoResult) return;

    if (undoResult.gameUndone) {
      if (gameHistoryRef.current && gameHistoryRef.current.length > 0) {
        gameHistoryRef.current = gameHistoryRef.current.slice(0, -1);
      }
    }

    const { newScore, newHistory, previousServer } = undoResult;
    const targetServer = previousServer || (undoResult.gameUndone ? getPreviousServer(server) : server);
    if (targetServer) {
      setLocalServer(targetServer);
    }

    const revertedPoints = newScore.points || [0, 0];
    const [u1, u2] = pointsToDisplay(revertedPoints[0], revertedPoints[1], newScore.isTieBreak);
    const revertedServingTeamNumber = isTeam1Serving(targetServer, team1) ? 1 : 2;
    announceClassicScore(u1, u2, newScore.isTieBreak, revertedServingTeamNumber);

    setLocalScore(newScore);
    setLocalPointHistory(newHistory);
    trackLocalWrite(() => persistUndoTransactional(newScore, newHistory, targetServer));
  };

  useEffect(() => {
    handlePointRef.current = handlePoint;
    handleUndoRef.current = handleUndo;
  });

  // Physical Keyboard / Hardware Button scoring
  useEffect(() => {
    const PRESS_WINDOW_MS = 400;
    const handleKeyDown = (e) => {
      if (e.repeat) return;
      const isEnterKey = e.key === 'Enter' || e.keyCode === 13;
      const isServingTeamKey = e.key === 'VolumeUp' || e.keyCode === 175 || e.key === 'ArrowUp';
      const isOtherTeamKey = e.key === 'VolumeDown' || e.keyCode === 176 || e.key === 'ArrowDown';

      if (isEnterKey) {
        e.preventDefault();
        e.stopPropagation();
        const { count, timeoutId } = pressPatternRef.current;
        if (timeoutId) clearTimeout(timeoutId);
        const newCount = count + 1;
        pressPatternRef.current.count = newCount;
        pressPatternRef.current.timeoutId = setTimeout(() => {
          pressPatternRef.current.timeoutId = null;
          pressPatternRef.current.count = 0;
          if (newCount === 1) handlePointRef.current?.(servingTeamNumber);
          else if (newCount === 2) handlePointRef.current?.(servingTeamNumber === 1 ? 2 : 1);
          else if (newCount >= 3) handleUndoRef.current?.();
        }, PRESS_WINDOW_MS);
        return;
      }
      if (isServingTeamKey) {
        e.preventDefault();
        e.stopPropagation();
        handlePointRef.current?.(servingTeamNumber);
      } else if (isOtherTeamKey) {
        e.preventDefault();
        e.stopPropagation();
        handlePointRef.current?.(servingTeamNumber === 1 ? 2 : 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (pressPatternRef.current?.timeoutId) clearTimeout(pressPatternRef.current.timeoutId);
    };
  }, [servingTeamNumber]);

  const handleDwellTrigger = useCallback((zoneId) => {
    console.log('🎯 HandTracker Dwell Triggered:', zoneId);
    if (celebrationData) return;

    if (zoneId === 'TEAM_A_HALF') {
      handlePointRef.current?.(1);
    } else if (zoneId === 'TEAM_B_HALF') {
      handlePointRef.current?.(2);
    } else if (zoneId === 'UNDO') {
      handleUndoRef.current?.();
    } else if (zoneId === 'SETTINGS') {
      setIsSettingsOpen(true);
      synth.playPoint('A');
    } else if (zoneId === 'FINISH_SESSION') {
      onFinishSession();
    }
  }, [celebrationData, onFinishSession]);

  return (
    <div className="relative w-screen h-screen bg-transparent text-slate-100 overflow-hidden flex flex-col font-sans select-none">
      
      {/* MediaPipe Hand Tracker Engine */}
      <HandTracker
        onCursorMove={setCursorData}
        onDwellTrigger={handleDwellTrigger}
        showCameraPreview={showCameraPreview}
        onCloseCameraPreview={() => setShowCameraPreview(false)}
        isHandTrackingEnabled={isHandTrackingEnabled}
        onStatusChange={setStatusText}
      />

      {/* Dwell Cursor Overlay */}
      {!celebrationData && isHandTrackingEnabled && (
        <DwellCursor cursorData={cursorData} />
      )}

      {/* Celebration Win Overlay */}
      <CelebrationOverlay
        eventData={celebrationData}
        onDismiss={() => setCelebrationData(null)}
      />

      {/* Padel Figma Scoreboard UI */}
      <main className="flex-1 w-full h-full">
        <PadelScoreBoard
          mode="classic"
          team1Players={team1}
          team2Players={team2}
          servingPlayer={server}
          team1ScoreDisplay={team1PointsDisplay}
          team2ScoreDisplay={team2PointsDisplay}
          sets={sets}
          games={games}
          setScores={activeScore?.setScores || []}
          isTieBreak={isTieBreak}
          allPlayers={allPlayers}
          onPointScore={(team) => handlePoint(team)}
          onUndo={handleUndo}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onFinishSession={onFinishSession}
          pairingCode={pairingCode}
          hoverZone={cursorData?.currentZone}
          celebrationData={celebrationData}
          bestOfSets={bestOfSets}
        />
      </main>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        isHandTrackingEnabled={isHandTrackingEnabled}
        setIsHandTrackingEnabled={setIsHandTrackingEnabled}
        showCameraPreview={showCameraPreview}
        setShowCameraPreview={setShowCameraPreview}
        goldenPoint={goldenPoint}
        onToggleGoldenPoint={handleToggleGoldenPoint}
        isSpeechEnabled={isSpeechEnabled}
        onToggleSpeech={handleToggleSpeech}
      />

    </div>
  );
}
